// util/AnnouncementContext.js
import { getAnnoucements } from "../API/announcements";

export function createAnnoucementContext(partitionKey, setVersion, options = {}) {
    const storageKey = options.storageKey ?? "announcements";
    const refreshMs = (options.refetchAnnoucementInterval ?? 60) * 1000;
    const ttlMs = options.ttlMs ?? 5 * 60 * 1000;

    let intervalId = null;
    let inFlight = null;
    let inMemory = null;
    let expiresAt = 0;
    let started = false;

    const hasLocalStorage = () => typeof window !== "undefined" && !!window.localStorage;

    function readCache() {
        try {
            if (!hasLocalStorage) {
                return null;
            }

            const raw = localStorage?.getItem(storageKey);

            if (!raw) {
                return null;
            }

            const parsed = JSON.parse(raw);
            const map = new Map(parsed.entries || []);

            return { map, expiresAt: parsed.expiresAt || 0 };
        } catch {
            return null;
        }
    }

    function writeCache(map) {
        try {
            if (!hasLocalStorage) {
                return;
            }

            expiresAt = Date.now() + ttlMs;
            localStorage?.setItem(
                storageKey,
                JSON.stringify({
                    entries: Array.from(map.entries()),
                    expiresAt: expiresAt,
                })
            );
        } catch { }
    }

    function touchCacheExpiry() {
        if (!hasLocalStorage) {
            return;
        }

        try {
            const raw = localStorage?.getItem(storageKey);

            if (!raw) {
                return null;
            }

            const parsed = JSON.parse(raw);

            expiresAt = Date.now() + ttlMs;
            parsed.expiresAt = expiresAt;

            localStorage?.setItem(
                storageKey,
                JSON.stringify(parsed)
            );
        } catch { }
    }

    function isExpired(ts) {
        return !ts || Date.now() >= ts;
    }

    async function fetchOnce(key = partitionKey) {
        if (inFlight) {
            return inFlight;
        }

        inFlight = (async () => {
            const resp = await getAnnoucements(key);
            const map = new Map();

            if (resp?.[0]?.id !== undefined) {
                const oldData = inMemory.get(key);

                if (isDifferent(oldData, resp[0])) {

                    map.set(key, resp[0]);

                    inMemory = map;

                    setVersion(prev => prev + 1);
                    writeCache(map);
                }
                else {
                    touchCacheExpiry();
                }
            }
            return map;
        })();

        try {
            return await inFlight;
        } finally {
            inFlight = null;
        }
    }

    function isDifferent(oldData, newData) {
        return (!oldData && newData) ||
            (!newData && oldData) ||
            (newData.isActive !== oldData.isActive) ||
            ((oldData?.content || "").trim() !== (newData?.content || "").trim());
    }

    function start() {
        if (started) {
            return;
        }

        started = true;

        const cached = readCache();

        if (cached) {
            inMemory = cached.map;
            expiresAt = cached.expiresAt;
        } else {
            inMemory = new Map();
            expiresAt = 0;
        }

        if (!intervalId) {
            intervalId = setInterval(() => {
                if (isExpired(expiresAt)) {
                    fetchOnce(partitionKey).catch(() => { });
                }
            }, refreshMs);
        }
    };

    return {
        async getAnnouncement(key = partitionKey, { fresh = false } = {}) {
            start();

            if (!fresh && inMemory?.has(key) && !isExpired(expiresAt)) {
                return inMemory.get(key);
            }

            await fetchOnce(key).catch(() => { });
            return inMemory.get(key);
        },

        refreshNow(key = partitionKey) {
            return fetchOnce(key);
        },

        stop() {
            if (intervalId) {
                clearInterval(intervalId);
            }

            intervalId = null;
        }
    };
}
